import {Box, Button, Typography} from "@mui/material";
import {FC} from "react";
import {makeStyles} from "tss-react/mui";
import {Footer} from "@/app2/shared/layouts/PageBase/Footer";
import {loginButtonSx} from "@/app2/shared/styles/loginButtonSx";

const LOGO_ICON = "/logo192.png";
const NO_ACCESS_IMAGE = "/no_access_image.svg";

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100dvh",
		width: "100%",
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#000000",
		color: "#FFFFFF"
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "16px 24px",
		flexShrink: 0,
		[theme.breakpoints.down("sm")]: {
			padding: "12px 16px"
		}
	},
	brand: {
		display: "flex",
		alignItems: "center",
		gap: "12px"
	},
	brandIcon: {
		width: "34px",
		height: "34px",
		objectFit: "contain",
		[theme.breakpoints.down("sm")]: {
			width: "26px",
			height: "26px"
		}
	},
	brandName: {
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 700,
		fontSize: "28px",
		lineHeight: 1,
		[theme.breakpoints.down("sm")]: {
			fontSize: "24px"
		}
	},
	main: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: "24px 24px 48px",
		textAlign: "center",
		[theme.breakpoints.down("sm")]: {
			padding: "16px 20px 32px",
			justifyContent: "flex-start",
			paddingTop: "32px"
		}
	},
	illustration: {
		width: "100%",
		maxWidth: "308px",
		height: "auto",
		marginBottom: "32px",
		[theme.breakpoints.down("sm")]: {
			maxWidth: "260px",
			marginBottom: "24px"
		}
	},
	title: {
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 600,
		fontSize: "48px",
		lineHeight: 1.1,
		letterSpacing: "-0.5px",
		marginBottom: "16px",
		[theme.breakpoints.down("md")]: {
			fontSize: "40px"
		},
		[theme.breakpoints.down("sm")]: {
			fontSize: "32px",
			marginBottom: "12px"
		}
	},
	primaryMessage: {
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 400,
		fontSize: "18px",
		lineHeight: 1.4,
		marginBottom: "16px",
		[theme.breakpoints.down("sm")]: {
			fontSize: "16px"
		}
	},
	secondaryMessage: {
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 400,
		fontSize: "14px",
		lineHeight: 1.5,
		color: "rgba(255, 255, 255, 0.65)",
		maxWidth: "520px"
	}
}));

export interface AccessRestrictedPageProps {
	onLogout: () => void;
}

export const AccessRestrictedPage: FC<AccessRestrictedPageProps> = ({onLogout}) => {
	const {classes} = useStyles();

	return (
		<Box className={classes.root}>
			<Box className={classes.header}>
				<Box className={classes.brand}>
					<img src={LOGO_ICON} alt="" className={classes.brandIcon} />
					<Typography component="span" className={classes.brandName}>
						Improve
					</Typography>
				</Box>
				<Button
					variant="contained"
					onClick={onLogout}
					sx={{...loginButtonSx, textTransform: "none"}}
				>
					Log Out
				</Button>
			</Box>

			<Box className={classes.main}>
				<Box
					component="img"
					src={NO_ACCESS_IMAGE}
					alt=""
					className={classes.illustration}
				/>
				<Typography component="h1" className={classes.title}>
					Confined Space
				</Typography>
				<Typography className={classes.primaryMessage}>
					It looks like your account does not have access.
				</Typography>
				<Typography className={classes.secondaryMessage}>
					If you believe this is an error, please contact your administrator or support team to
					request access. Be sure to include the email address you&apos;re using to sign in.
				</Typography>
			</Box>

			<Footer />
		</Box>
	);
};
